// src/components/AgriMetaverseMap.jsx
import React, { useMemo, useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinus, faPlus, faTimes, faCopy } from '@fortawesome/free-solid-svg-icons';

/**
 * Props:
 *  - rows (number) default 6
 *  - cols (number) default 8
 *  - dataset (array) default []  // listing objects: { id, name, price, img, owner, coords: {r,c}, type, location, area, marketPrice, offerPrice, unit }
 *  - ownerId (string) optional - highlights player's owned plots
 *  - onContact(listing) optional callback
 *  - onMakeOffer(listing, amount) optional callback
 */
export default function AgriMetaverseMap({
  rows = 6,
  cols = 8,
  dataset = [],
  ownerId = null,
  onContact = (it) => alert(`Contact requested for ${it.name || it.id} (dummy)`),
  onMakeOffer = (it, amount) => alert(`Offer ${amount} placed for ${it.name || it.id} (dummy)`),
}) {
  const [zoom, setZoom] = useState(1);
  const [showOwners, setShowOwners] = useState(true);
  const [showHeatmap, setShowHeatmap] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedCell, setSelectedCell] = useState(null);
  const [mapGrid, setMapGrid] = useState([]);
  const [colorScaleDomain, setColorScaleDomain] = useState([0, 1000]);
  const containerRef = useRef(null);
  const touchState = useRef({ initialDistance: null, initialZoom: null });
  const listings = dataset || [];

  // Compute min/max price for heatmap
  useEffect(() => {
    const prices = listings
      .map(l => Number(l.price || l.offerPrice || l.marketPrice || 0))
      .filter(p => !isNaN(p) && p > 0);
    if (prices.length) setColorScaleDomain([Math.min(...prices), Math.max(...prices)]);
  }, [dataset]);

  // Build grid
  useEffect(() => {
    const total = rows * cols;
    const grid = Array.from({ length: rows }).map(() =>
      Array.from({ length: cols }).map(() => ({ items: [] }))
    );
    const clamp = (v, max) => Math.max(0, Math.min(max - 1, v));
    let idx = 0;
    for (const item of listings) {
      if (item?.coords?.r !== undefined && item?.coords?.c !== undefined) {
        const r = clamp(item.coords.r, rows);
        const c = clamp(item.coords.c, cols);
        grid[r][c].items.push(item);
      } else {
        const pos = idx % total;
        const r = Math.floor(pos / cols);
        const c = pos % cols;
        grid[r][c].items.push(item);
        idx++;
      }
    }
    setMapGrid(grid);
  }, [dataset, rows, cols, listings]);

  // Price to color gradient
  const priceToColor = (price) => {
    const [min, max] = colorScaleDomain;
    if (!price || isNaN(price)) return '#1f1f1f';
    const t = max === min ? 0.5 : Math.max(0, Math.min(1, (price - min) / (max - min)));
    const lerp = (a, b, v) => Math.round(a + (b - a) * v);
    const green = [37, 160, 50];
    const yellow = [255, 200, 60];
    const red = [225, 50, 50];
    const rgb = t < 0.5
      ? [lerp(green[0], yellow[0], t / 0.5), lerp(green[1], yellow[1], t / 0.5), lerp(green[2], yellow[2], t / 0.5)]
      : [lerp(yellow[0], red[0], (t - 0.5) / 0.5), lerp(yellow[1], red[1], (t - 0.5) / 0.5), lerp(yellow[2], red[2], (t - 0.5) / 0.5)];
    return `rgb(${rgb.join(',')})`;
  };

  const totalItems = useMemo(() => listings.length, [listings]);
  const cellMatchesSearch = (cell) => {
    if (!search) return true;
    const q = search.trim().toLowerCase();
    return cell.items.some(it =>
      it.name?.toLowerCase().includes(q) ||
      it.location?.toLowerCase().includes(q) ||
      it.area?.toLowerCase().includes(q)
    );
  };

  const openCell = (r, c) => {
    const items = mapGrid[r]?.[c]?.items || [];
    setSelectedCell({ r, c, items });
  };
  const closeDetails = () => setSelectedCell(null);
  const makeOffer = (listing) => {
    const amount = window.prompt(`Enter your offer amount for ${listing.name || listing.id}:`, listing.price || listing.offerPrice || '');
    if (!amount) return;
    onMakeOffer(listing, amount);
  };
  const contactOwner = (listing) => onContact(listing);
  const scheduleVisit = (listing) => {
    const dt = window.prompt('Preferred visit date/time (dummy):', '');
    if (!dt) { alert('Visit cancelled'); return; }
    alert(`Visit scheduled (dummy) for ${listing.name || listing.id} on ${dt}`);
  };

  // Responsive width
  const [containerWidth, setContainerWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200);
  const containerSizeRef = useRef(containerWidth);
  useEffect(() => {
    const updateWidth = () => {
      const w = containerRef.current?.clientWidth || window.innerWidth;
      setContainerWidth(w);
      containerSizeRef.current = w;
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    let ro;
    if (containerRef.current && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(updateWidth);
      ro.observe(containerRef.current);
    }
    return () => {
      window.removeEventListener('resize', updateWidth);
      if (ro && containerRef.current) ro.unobserve(containerRef.current);
    };
  }, []);

  const BASE_CELL = 120;
  const cellMin = useMemo(() => {
    const maxAllowed = Math.floor((containerWidth - 12) / cols);
    const desired = Math.round(BASE_CELL * zoom);
    return Math.max(72, Math.min(desired, Math.max(72, maxAllowed)));
  }, [containerWidth, cols, zoom]);

  const clampZoomForContainer = (z) => {
    const maxAllowed = Math.max(72, Math.floor((containerWidth - 12) / cols));
    const maxZoom = Math.max(0.6, maxAllowed / BASE_CELL);
    return Math.max(0.5, Math.min(z, maxZoom * 2.5));
  };

  // Touch pinch-to-zoom
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const getDistance = (touches) => {
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.sqrt(dx * dx + dy * dy);
    };

    const onTouchStart = (e) => {
      if (e.touches?.length === 2) {
        touchState.current.initialDistance = getDistance(e.touches);
        touchState.current.initialZoom = zoom;
      }
    };

    const onTouchMove = (e) => {
      if (e.touches?.length === 2 && touchState.current.initialDistance) {
        if (e.cancelable) e.preventDefault();
        const newDist = getDistance(e.touches);
        setZoom(clampZoomForContainer(touchState.current.initialZoom * (newDist / touchState.current.initialDistance)));
      }
    };

    const onTouchEnd = () => {
      touchState.current.initialDistance = null;
      touchState.current.initialZoom = null;
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    el.addEventListener('touchcancel', onTouchEnd, { passive: true });

    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchEnd);
    };
  }, [zoom, containerWidth, cols]);

  if (!mapGrid?.length) return <div className="p-3 text-gray-300">Loading map...</div>;

  return (
    <div className="mt-1 w-full overflow-x-hidden" ref={containerRef}>
      {/* Controls */}
      <div className="agmap-controls flex flex-wrap gap-2 items-center p-2 rounded-lg border border-green-500 bg-black text-gray-300">
        <div className="flex items-center gap-2">
          <button onClick={() => setZoom(z => clampZoomForContainer(z - 0.1))}
                  className="px-2 py-1 rounded border border-green-500 hover:bg-green-500 hover:text-black">
            <FontAwesomeIcon icon={faMinus} />
          </button>
          <span className="w-12 text-center">{Math.round(zoom * 100)}%</span>
          <button onClick={() => setZoom(z => clampZoomForContainer(z + 0.1))}
                  className="px-2 py-1 rounded border border-green-500 hover:bg-green-500 hover:text-black">
            <FontAwesomeIcon icon={faPlus} />
          </button>
        </div>

        <div className="flex items-center gap-4">
          <label className="flex items-center gap-1">
            <input type="checkbox" checked={showOwners} onChange={e => setShowOwners(e.target.checked)} className="accent-green-500" /> Owners
          </label>
          <label className="flex items-center gap-1">
            <input type="checkbox" checked={showHeatmap} onChange={e => setShowHeatmap(e.target.checked)} className="accent-green-500" /> Heatmap
          </label>
        </div>

        <div className="flex-1" />

        <div className="flex items-center gap-2">
          <div className="w-32 h-2 rounded" style={{ background: 'linear-gradient(90deg,#25a032,#ffc83c,#e13232)' }} />
          <span className="text-gray-400 text-sm">Price heat</span>
          <input
            type="text"
            placeholder="Search..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="px-2 py-1 rounded border border-gray-700 bg-black text-green-500 placeholder-gray-400"
          />
        </div>
      </div>

      {/* Grid */}
      <div className="agmap-grid p-2 rounded-lg border border-green-500 bg-black">
        <div className="agmap-grid-content grid gap-2"
             style={{ gridTemplateColumns: `repeat(${cols}, minmax(${cellMin}px, 1fr))` }}>
          {mapGrid.map((row, r) =>
            row.map((cell, c) => {
              const items = cell.items || [];
              const visible = cellMatchesSearch(cell);
              const prices = items.map(it => Number(it.price || it.offerPrice || it.marketPrice || 0)).filter(p => p > 0);
              const avgPrice = prices.length ? Math.round(prices.reduce((a, b) => a + b, 0) / prices.length) : null;
              const heatColor = avgPrice && showHeatmap ? priceToColor(avgPrice) : '#111';
              const ownedByPlayer = items.some(it => it.owner && ownerId && it.owner === ownerId);
              const anyOwned = items.some(it => it.owner);

              return (
                <div
                  key={`${r}-${c}`}
                  className="plot rounded-lg border border-green-500 flex flex-col items-center justify-center cursor-pointer select-none"
                  style={{
                    minHeight: visible ? undefined : 84,
                    opacity: visible ? 1 : 0.2,
                    backgroundColor: heatColor,
                  }}
                  onClick={() => openCell(r, c)}
                  title={`${items.length} item(s)` + (avgPrice ? ` • avg ₹${avgPrice}` : '')}
                >
                  {items.length > 0 && <div className="badge absolute top-1 right-1 bg-black text-green-500 px-2 py-0.5 rounded-full text-xs">{items.length}</div>}
                  <div className="plot-inner flex flex-col items-center justify-center p-1">
                    {items.length > 0 ? (
                      <>
                        <img className="plot-thumb w-12 h-12 rounded shadow-md" src={items[0].img || `https://via.placeholder.com/80?text=${encodeURIComponent(items[0].name||'')}`} alt={items[0].name} />
                        <div className="plot-name text-gray-300 font-semibold text-sm truncate">{items[0].name || items[0].id}</div>
                        <div className="plot-sub text-green-500 text-xs">{items[0].location || items[0].area || ''}</div>
                        {avgPrice && <div className="text-gray-400 text-xs mt-1">avg ₹{avgPrice}</div>}
                      </>
                    ) : (
                      <>
                        <div className="text-green-500 text-2xl">🟩</div>
                        <div className="text-gray-500 text-xs mt-1">Unowned</div>
                      </>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Details drawer */}
      {selectedCell && (() => {
        const isNarrow = window.innerWidth < 760;
        const drawerStyle = isNarrow
          ? 'fixed left-2 right-2 bottom-2 h-2/5 z-50 bg-gray-900 shadow-lg rounded-lg p-3 overflow-auto'
          : 'fixed right-2 top-20 bottom-5 w-80 z-50 bg-gray-900 shadow-lg rounded-lg p-3 overflow-auto';

        return (
          <div className={drawerStyle}>
            <div className="flex justify-between items-center text-gray-300">
              <strong className="text-gray-300">Cell {selectedCell.r + 1}, {selectedCell.c + 1}</strong>
              <div className="flex gap-2">
                <button onClick={() => { navigator.clipboard?.writeText(`Cell ${selectedCell.r+1},${selectedCell.c+1}`); alert('Copied'); }} className="px-2 py-1 rounded border border-green-500 hover:bg-green-500 hover:text-black">
                  <FontAwesomeIcon icon={faCopy} />
                </button>
                <button onClick={closeDetails} className="px-2 py-1 rounded border border-red-500 hover:bg-red-500 hover:text-black">
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
            </div>

            <div className="mt-2 space-y-2">
              {selectedCell.items.length === 0 ? (
                <div className="text-gray-500">No items in this cell</div>
              ) : selectedCell.items.map(it => (
                <div key={it.id || it.name} className="flex gap-2 p-2 border-b border-gray-700 items-center">
                  <img src={it.img || `https://via.placeholder.com/80?text=${encodeURIComponent(it.name||'')}`} alt={it.name} className="w-16 h-12 object-cover rounded" />
                  <div className="flex-1 text-gray-300">
                    <div className="font-semibold">{it.name || it.id}</div>
                    <div className="text-green-500 text-xs">{it.location || it.area || ''}</div>
                    <div className="mt-1">
                      <span className={`text-xs ${it.marketPrice && it.marketPrice > (it.offerPrice || 0) ? 'line-through text-gray-500' : 'text-gray-400'}`}>₹{it.marketPrice || it.price || '-'}</span>
                      <span className="font-bold text-green-500 ml-1">₹{it.offerPrice || it.price || '-'}</span>
                      {it.unit && <span className="text-gray-500 text-xs ml-1">/ {it.unit}</span>}
                    </div>
                  </div>
                  <div className="flex flex-col gap-1">
                    <button onClick={() => contactOwner(it)} className="px-2 py-1 rounded bg-green-600 text-black hover:bg-green-500">Contact</button>
                    <button onClick={() => scheduleVisit(it)} className="px-2 py-1 rounded bg-gray-800 text-green-500 border border-green-500 hover:bg-green-500 hover:text-black">Visit</button>
                    <button onClick={() => makeOffer(it)} className="px-2 py-1 rounded bg-yellow-500 text-black hover:bg-yellow-400">Make Offer</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}
    </div>
  );
}
